<script lang="ts">
  import Arrow from "$components/arrow/index.svelte";
  import DateComp from "$components/date-on-card/index.svelte";
  import LinkComp from "$components/link.svelte";
  import ImageComp from "$components/image.svelte";
  import type { ITicketSchema } from "$lib/utils/ldjson";
  import type { ITPUpcomingGames } from "types/backend/models/widgets/upcoming-games.entity";
  import { serializeSchema, ticketToSchema } from "$lib/utils/ldjson";

  export let upcominggames: ITPUpcomingGames[] = [];
  export let locale = "en";

  function toSchema(game: ITPUpcomingGames) {
    const row = {
      ...game,
      qty: game.product.availableQty,
      datetime: new Date(game?.datetime),
      priceFrom: undefined,
    };
    return serializeSchema(ticketToSchema(row as unknown as ITicketSchema));
  }

  let mobileVisible = 4;
  function loadMore(ev: Event) {
    ev.stopImmediatePropagation();
    mobileVisible += 5;
  }
</script>

<svelte:head>
  {#each upcominggames as ticket}
    {@html toSchema(ticket)}
  {/each}
</svelte:head>

<section class="ticket-container">
  {#each upcominggames as item, i}
    {@const { title, datetime, subcategory, venue, slug, tbc, product, icon, performers } = item}
    {@const { title: venueTitle, address } = venue}
    <LinkComp href={slug} className={i > mobileVisible ? "desk-only" : ""}>
      <article class="single-tickets grid">
        <DateComp date={datetime} {tbc} className="transparent-desk-only" />
        <div class="content grid">
          <div class="logos">
            {#if performers.length}
              {#each performers as perf}
                <ImageComp image={perf?.attributes?.logo || {}} />
              {/each}
            {/if}
          </div>
          <h3 class="pf-500">{title}</h3>
          <p class="pf-300 subcategory">{subcategory?.title || ""}</p>
          <p class="pf-500 cut-text" title={venue?.address?.city}>
            {[venueTitle, address?.city].filter(v => v).join(", ")}
          </p>
        </div>
        <div class="extras">
          <div class={`icon ${icon}`}>
            <i />
            <p class="pf-500">{icon.replace("-", " ")}</p>
          </div>
          <div class="icon cards">
            <i />
            {#if product?.availableQty}
              <p class="pf-400">{product?.availableQty} Ticket{product?.availableQty > 1 ? "s" : ""} available</p>
            {:else}
              <p class="pf-400">No ticket available</p>
            {/if}
          </div>
        </div>
        <div class="arrow-wrapper grid">
          <div>
            <Arrow />
          </div>
        </div>
      </article>
    </LinkComp>
  {/each}
  {#if mobileVisible < upcominggames.length}
    <button class="mob-only btn load-more" on:click={loadMore}>Load More</button>
  {/if}
</section>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;

  .ticket-container {
    h3,
    p {
      margin: 0;
    }
    @include desktop {
      grid-template-columns: 1fr;
      background: #f8f8f8;
      border-radius: 0.312em;
    }

    & :global(a:last-child .single-tickets) {
      border: 1px solid #ededed !important;
    }
  }

  .single-tickets {
    border-radius: 0.4em;
    margin: 0.4em 0;
    grid-template-columns: 3.125em auto 1em;
    align-items: center;
    border: 1px solid #ededed;
    padding: 0.625em;
    @include desktop {
      border-radius: 0;
      border-bottom: none;
      margin: 0;
      padding: 0;
      height: 6.8em;
      grid-template-columns: 6.44em auto 14em 6.25em;
    }
  }
  .content {
    grid-template-columns: 1fr;
    gap: 0.3em;
    @include desktop {
      gap: 0.5em;
    }
    h3 {
      font-size: 1em;
      line-height: 1.0625em;
      @include desktop {
        align-self: center;
        font-size: 1.25em;
      }
    }
    p {
      line-height: 1em;
      font-size: 0.813em;
      @include desktop {
        font-size: 1em;
      }
    }
  }

  .logos {
    display: flex;
    gap: 0.5em;
    width: 3.65em;
    height: 1.7em;
    :global(picture) {
      width: 50%;
    }
    :global(.team-logo) {
      width: 1.7em !important;
      height: 1.7em !important;
    }
    @include desktop {
      display: none;
    }
  }

  .sep {
    margin: auto;
    width: 2.1875em;
    height: 0.0625em;
  }

  .cut-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .extras {
    display: none;
    @include desktop {
      display: grid;
      border-left: 1px solid #ededed;
      height: 100%;
      grid-template-columns: 1fr;
      justify-content: center;
      align-content: center;
      gap: 1em;
      padding-left: 2em;

      .icon {
        display: grid;
        grid-template-columns: 1.5625em auto;
        gap: 0.5em;

        p {
          margin: 0;
          font-size: 0.875em; // 14px
          text-transform: capitalize;
        }

        &.icon > i {
          background-repeat: no-repeat;
          background-position: center;
          background-size: contain;
        }

        &.cards {
          i {
            width: 17px;
            height: 18px;
            background: url(./assets/cards.svg) no-repeat center center;
          }
        }

        &.sale-ending-soon {
          color: #c90000;
          i {
            width: 9px;
            height: 15px;
            background-image: url(./assets/sale-ending-soon.svg);
            filter: invert(25%) sepia(100%) saturate(7468%) hue-rotate(354deg) brightness(93%) contrast(104%);
          }
        }
        &.price-increase {
          color: #db5103;
          i {
            width: 20px;
            height: 12px;
            background-image: url(./assets/price-increase.svg);
            filter: invert(47%) sepia(91%) saturate(1167%) hue-rotate(354deg) brightness(96%) contrast(101%);
          }
        }
        &.excellent-value {
          color: #00a106;
          i {
            width: 18px;
            height: 18px;
            background-image: url(./assets/excellent-value.svg);
            filter: invert(33%) sepia(92%) saturate(3094%) hue-rotate(87deg) brightness(92%) contrast(102%);
          }
        }
        &.tickets-selling-fast {
          color: #db5103;
          i {
            width: 18px;
            height: 14px;
            background-image: url(./assets/tickets-selling-fast.svg);
            filter: invert(47%) sepia(91%) saturate(1167%) hue-rotate(354deg) brightness(96%) contrast(101%);
          }
        }
        &.popular-game {
          color: #ca0090;
          i {
            width: 22px;
            height: 15px;
            background-image: url(./assets/popular-game.svg);
            filter: invert(39%) sepia(100%) saturate(312%) hue-rotate(292deg) brightness(92%) contrast(92%);
          }
        }
      }
    }
  }

  .arrow-wrapper {
    @include desktop {
      border-left: 1px solid #ededed;
      height: 100%;
    }
    div {
      justify-self: end;
      @include desktop {
        justify-self: center;
        align-self: center;
      }
    }
  }

  .load-more {
    width: 100%;
    color: #fff;
    font-family: Nunito;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 30px; /* 187.5% */
  }
</style>
